import { v1 as uuidv1 } from 'uuid';
import _isEmpty from 'lodash/isEmpty';
import generateFormEventId from './generateFormEventId';
import saveDealFromLeadForm from './saveDealFromLeadForm';

/**
 * sendEmailAndSaveDeal is a utility that takes parameters to create an email data object that
 * will be used to create the payload for the emailOwnerFetcher. This utility also creates a deal
 * if a saveDealXgFetcher is passed in, otherwise it will just create the email data object, and
 * call the emailOwnerFetcher to send the email. It returns the object sent to the fetcher as emailData,
 * the return from calling the fetcher as emailResult, and the dealXgId returned from the saveDeal as
 * responseDealXgId.
 *
 * @param {string} clickType - The property to be sent to the fetcher for analytics
 * @param {string} consumerBrand - The brand code that the product is being sent from
 * @param {Object} cookies - An object containing cookie data from the user
 * @param {boolean} copySelf - Leads feature flag that should be passed to the emailOwnerFetcher
 * @param {Object} deal - An object containing the deal data for the lead
 * @param {Object} dealXgIdRef - An object containing the current dealXgId
 * @param {Object} dealsDuck -  Deals duck object
 * @param {boolean} disableTrade - Listing value that disables trade info when sending lead
 * @param {function} dispatch - The function that sends the email lead
 * @param {function}  emailOwnerFetcher - The function that sends the email
 * @param {boolean} enableSaveDeal - The boolean that determines whether a deal will be saved
 * @param {string} env - An object containing the form data of lead profile data that will be used to create the email lead
 * @param {Object} formData - An object containing the form data of lead profile data that will be used to create the email lead
 * @param {Object} icoTradeIn - An object containing the instant cash offer data
 * @param {boolean} includeWalletData - A bollean of whether the wallet data will be sent wwith the email lead
 * @param {boolean} isAccelerate - Identifies if the lead is for and Accelerate listing
 * @param {boolean} isBuildAndPrice - Identifiesr if the email is for a build and price page listing
 * @param {boolean} isQuote - Identifies if the email is being sent from a quote context
 * @param {boolean} isScheduleTestDrive - Identifies if the email is to schedule a test drive from the inquiry list
 * @param {boolean} isWallet - Identifies if the email is being sent from wallet
 * @param {Object} listing - An object containg the listing information
 * @param {boolean} oneClick - Identifier for if the email is being sent from a oneClick CTA
 * @param {string} originPage - String that contains which page the lead is being sent from
 * @param {Object} owner - An object containg the owner data for the listing
 * @param {string} partnerName - String containg the parter for the page the email is being sent on
 * @param {Object} paymentsDuck - Payments duck object
 * @param {string} pixallFirstPartyId - String containing the pixall data for analytics
 * @param {string} pixallThirdPartyId - String containing the pixall data for analytics
 * @param {Object} preorderContent - Object containing the data for a preorder lead
 * @param {function} saveDealXgFetcher -The fetcher that will save the deal and create a new dealXg Id
 * @param {Object} selectorDuck - Selector duck object
 * @param {string} serviceInquiry - The service inquiry for the lead
 * @param {string} userZip - The zip code of the user
 * @param {string} vdpType - the product type of the vdp
 * @param {string} walletId - The wallet Id of the user sending the lead
 *
 * @returns {Object} An object containg email data, email result from fetcher, and dealxgId
 */

export default async function sendEmailAndSaveDeal(_ref) {
  let {
    clickType,
    consumerBrand,
    cookies,
    copySelf,
    deal,
    dealXgIdRef,
    dealsDuck,
    disableTrade,
    dispatch,
    emailOwnerFetcher = () => {},
    enableSaveDeal,
    env,
    formData,
    icoTradeIn,
    includeWalletData,
    isAccelerate,
    isBuildAndPrice,
    isQuote,
    isScheduleTestDrive,
    isWallet,
    listing,
    oneClick,
    originPage,
    owner,
    partnerName,
    paymentsDuck,
    pixallFirstPartyId,
    pixallThirdPartyId,
    preorderContent,
    saveDealXgFetcher,
    selectorDuck,
    serviceInquiry,
    userZip,
    vdpType,
    walletId
  } = _ref;
  let responseDealXgId = dealXgIdRef?.current;
  if (saveDealXgFetcher) {
    responseDealXgId = await saveDealFromLeadForm({
      consumerBrand,
      enableSaveDeal,
      deal,
      isAccelerate,
      formData,
      dealXgId: dealXgIdRef.current,
      listing,
      zip: userZip,
      walletId,
      isScheduleTestDrive,
      pixallId: pixallThirdPartyId || pixallFirstPartyId,
      saveDealXgFetcher,
      paymentsDuck,
      dealsDuck,
      selectorDuck,
      icoTradeIn,
      dispatch,
      env
    });
  }
  const {
    id: listingId,
    listingTitle,
    make = '',
    ownerId,
    model = '',
    trim = '',
    year: modelYear,
    vin
  } = listing;
  const adobeTransId = uuidv1();
  let jornayaToken = '';
  let jornayaLeadId = '';
  if (!oneClick) {
    jornayaToken = Object.hasOwn(formData, 'TCPA_OptIn') && owner?.tcpaAuditVendor !== 'AUTOTRADER' ? document.getElementById('leadid_token').value : '';
    jornayaLeadId = owner?.hasTCPA && !jornayaToken ? 'Jornaya Universal LeadiD not available.' : jornayaToken;
  }
  const formEventId = oneClick ? generateFormEventId(formData?.comments, formData?.fromEmailAddress, listingId, listing?.owner?.id) : generateFormEventId(formData?.comments, formData?.fromEmailAddress, listingId, owner?.id);
  const emailData = {
    ...formData,
    bookmarkTitle: listingTitle,
    comments: formData?.comments ?? 'Please confirm vehicle availability.',
    ipAddress: cookies?.pxa_ipv4,
    cookieId: cookies?.ATC_ID,
    subject: isQuote || isBuildAndPrice ? 'New Car Custom Order Inquiry' : 'General Vehicle Info',
    phoneNumber: parseInt(formData?.phoneNumber?.replace(/\D+/g, ''), 10),
    listingId: isQuote || isBuildAndPrice ? '' : listingId,
    includeWalletData: originPage === 'srp' && oneClick ? false : includeWalletData,
    pixallThirdPartyId,
    pixallFirstPartyId,
    adobeTransId: `Lead_${adobeTransId}`,
    jornayaLeadId,
    ownerId: oneClick ? listing?.owner?.id || ownerId : owner?.id,
    searchZip: userZip,
    partnerName,
    clickType: clickType || listing?.clickType,
    copySelf,
    vdpType: vdpType || '',
    originPage,
    deal: originPage === 'srp' && oneClick ? {} : {
      ...deal
    },
    formEventId,
    ...serviceInquiry,
    ...(preorderContent && !_isEmpty(preorderContent) && {
      preorderInfo: preorderContent,
      make,
      model,
      modelYear,
      trim
    }),
    dealXgId: originPage === 'srp' && oneClick ? undefined : responseDealXgId,
    vin: isWallet ? vin : undefined,
    ...(isBuildAndPrice && owner?.customOrderStatus?.newCarBoost && {
      leadType: 'newcarboost'
    }),
    oneClick
  };
  if (disableTrade && Object.keys(emailData.deal)) {
    Object.keys(emailData.deal).forEach(key => {
      if (key.startsWith('tradeIn')) delete emailData.deal[key];
    });
  }
  const emailResult = await emailOwnerFetcher(emailData);
  return {
    emailData,
    emailResult,
    responseDealXgId
  };
}